import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import {
  createDiscount,
  deleteDiscount,
  generateDiscountCode,
  getDiscount,
  getDiscounts,
  updateDiscount,
  type DiscountIndexKeyEntries,
  type DiscountOrganizationScope,
  type DiscountScope,
  type DiscountShowKeyEntries,
} from './discounts.api'
import type {
  CreateDiscountInput,
  DeleteDiscountResponse,
  Discount,
  DiscountIdInput,
  DiscountIndexResponse,
  DiscountIndexSort,
  GenerateDiscountCodeInput,
  UpdateDiscountInput,
} from './discounts.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface DiscountIndexFilters {
  search?: string | undefined
  sort?: DiscountIndexSort | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type DiscountIndexQueryKey = [AdminQueryRoot & DiscountIndexKeyEntries]
type DiscountShowQueryKey = [AdminQueryRoot & DiscountShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const discountQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'discount' }, { organizationId }),
  ],
  index: ({
    organizationId,
    search,
    sort,
    page,
    pageSize,
  }: OrganizationScope & DiscountIndexFilters = {}): DiscountIndexQueryKey =>
    buildScopedAdminQueryKey(discountQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      search,
      sort,
      page,
      pageSize,
    }),
  show: ({
    organizationId,
    discountId,
  }: OrganizationScope & { discountId: DiscountIdInput }): DiscountShowQueryKey =>
    buildScopedAdminQueryKey(discountQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      discountId: Number(discountId),
    }),
}

export const discountQueries = {
  index: ({
    organizationId,
    search,
    sort,
    page,
    pageSize,
  }: DiscountOrganizationScope & DiscountIndexFilters) =>
    indexTableQueryOptions({
      queryKey: discountQueryKeys.index({ organizationId, search, sort, page, pageSize }),
      queryFn: getDiscounts,
    }),
  show: ({ organizationId, discountId }: DiscountScope) =>
    queryOptions({
      queryKey: discountQueryKeys.show({ organizationId, discountId }),
      queryFn: getDiscount,
      placeholderData: () => {
        const cachedIndexQueries = queryClient.getQueriesData<DiscountIndexResponse>({
          queryKey: discountQueryKeys.index({ organizationId }),
        })

        for (const [, response] of cachedIndexQueries) {
          const cachedDiscount = response?.data.find(
            (discount) => discount.id === Number(discountId)
          )

          if (cachedDiscount) {
            return cachedDiscount
          }
        }

        return undefined
      },
    }),
}

export function useDiscounts({
  organizationId,
  search,
  sort,
  page,
  pageSize,
  ...params
}: OrganizationScope &
  DiscountIndexFilters &
  QueryParams<ReturnType<typeof discountQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...discountQueries.index({
      organizationId: resolvedOrganizationId,
      search,
      sort,
      page,
      pageSize,
    }),
    ...params,
  })
}

export function useDiscount({
  organizationId,
  discountId,
  ...params
}: OrganizationScope & {
  discountId: DiscountIdInput
} & QueryParams<ReturnType<typeof discountQueries.show>>) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...discountQueries.show({
      organizationId: resolvedOrganizationId,
      discountId,
    }),
    ...params,
  })
}

function useDiscountMutation<TInput extends object, TResult>(
  mutationFn: (input: DiscountOrganizationScope & TInput) => Promise<TResult>
) {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: TInput) => mutationFn({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: discountQueryKeys.all({ organizationId }) }),
  })
}

export function useCreateDiscountMutation() {
  return useDiscountMutation<CreateDiscountInput, Discount>(createDiscount)
}

export function useUpdateDiscountMutation() {
  return useDiscountMutation<UpdateDiscountInput & { discountId: DiscountIdInput }, Discount>(
    updateDiscount
  )
}

export function useDeleteDiscountMutation() {
  return useDiscountMutation<{ discountId: DiscountIdInput }, DeleteDiscountResponse>(
    deleteDiscount
  )
}

export function useGenerateDiscountCodeMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: GenerateDiscountCodeInput) =>
      generateDiscountCode({ organizationId, ...values }),
  })
}
